import { KBIcon } from "~/components/knowledgeBase/KBIcon"
import { KBIconPicker } from "~/components/knowledgeBase/KBIconPicker"
import { usePostHog } from "posthog-js/react"
import type { useOnboardingData } from "~/hooks/useOnboardingData"
import type { ReactNode } from "react"
import {
	EyeIcon,
	PencilIcon,
	HistoryIcon,
	InfoIcon,
	LinkIcon,
	PinIcon,
	ShieldIcon,
	StarIcon,
	Trash2Icon
} from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { AppHeader } from "~/components/AppHeader"
import { getEditorContentFingerprint } from "~/lib/editor-fingerprint"
import { Editor } from "~/components/editor/Editor"
import { Capacitor } from "@capacitor/core"
import { CollaborativeEditor } from "~/components/editor/CollaborativeEditor"
import { KBHistoryDialog } from "~/components/knowledgeBase/KBHistoryDialog"
import { KBRolePicker } from "~/components/knowledgeBase/KBRolePicker"
import { KBShareDialog } from "~/components/knowledgeBase/KBShareDialog"
import { LoadingState } from "~/components/screens/StateScreen"
import { UnsavedChanges } from "~/components/UnsavedChanges"
import { EntityIdentity } from "~/components/EntityIdentity"
import { Button, TooltipButton } from "~/components/ui/button"
import { Input } from "~/components/ui/input"
import { Textarea } from "~/components/ui/textarea"
import { usePermission } from "~/hooks/usePermission"
import { useTeam } from "~/hooks/useTeam"
import { confirmAction } from "~/lib/native-dialog"
import { ContentTemplateKind } from "~/lib/enums"
import { TeamPermissions } from "~/lib/permissions"
import { cn, normalizeIssueDescription } from "~/lib/utils"
import { api } from "~/trpc/react"

export default function Page({
	practice,
	tutorialPrompt,
	tutorialActions
}: {
	practice?: ReturnType<typeof useOnboardingData>
	tutorialPrompt?: ReactNode
	tutorialActions?: ReactNode
} = {}) {
	const {
		orgSlug = "",
		teamSlug = "",
		documentId = ""
	} = useParams<{
		orgSlug: string
		teamSlug: string
		documentId: string
	}>()
	const liveTeam = useTeam()
	const team = practice?.team ?? liveTeam.team
	const isLoading = !practice && liveTeam.isLoading
	const { hasTeamPermission } = usePermission()
	const canEdit =
		!!practice || hasTeamPermission(TeamPermissions.EDIT_KNOWLEDGE_BASE)
	const canComment =
		!!practice || hasTeamPermission(TeamPermissions.COMMENT_KNOWLEDGE_BASE)
	const commentRole = canEdit
		? "editor"
		: canComment
			? ("comment" as const)
			: undefined
	const DocumentEditor =
		practice || Capacitor.isNativePlatform() ? Editor : CollaborativeEditor
	const manualBody = !!practice || Capacitor.isNativePlatform()
	const bodyRevisionRef = useRef(0)
	const navigate = useNavigate()
	const posthog = usePostHog()
	const [editingDocumentId, setEditingDocumentId] = useState<string | null>(
		null
	)
	const utils = api.useUtils()
	const liveDocument = api.knowledgeBase.get.useQuery(
		{ teamId: team?.id ?? "", documentId },
		{ enabled: !practice && !!team?.id && !!documentId }
	)
	const document = practice
		? { data: practice.document, isLoading: false }
		: liveDocument
	const [documentState, setDocumentState] = useState<{
		id: string
		title: string
		description: string
		category: string
		icon: string
		content: string | null
		accessRoleIds: string[]
		updatedAt: Date
	} | null>(null)
	const lastSyncedDocument = useRef<typeof documentState>(null)

	const liveEditDocument = api.knowledgeBase.edit.useMutation({
		onSuccess: async (data) => {
			if (data?.slug && data.slug !== documentId && data.id !== documentId) {
				navigate(`/${orgSlug}/${teamSlug}/kb/${data.slug}`, { replace: true })
			}
			await utils.favorites.list.invalidate()
			await utils.knowledgeBase.get.invalidate({
				teamId: team?.id ?? ""
			})
			await utils.knowledgeBase.list.invalidate({ teamId: team?.id ?? "" })
			toast("Document saved")
		}
	})
	const editDocument = practice
		? {
				isPending: false,
				mutateAsync: practice.editDocument,
				mutate: (input: Parameters<typeof practice.editDocument>[0]) => {
					void practice.editDocument(input)
				}
			}
		: liveEditDocument
	const deleteDocument = api.knowledgeBase.delete.useMutation({
		onSuccess: async () => {
			await utils.favorites.list.invalidate()
			await utils.knowledgeBase.list.invalidate({ teamId: team?.id ?? "" })
			navigate(`/${orgSlug}/${teamSlug}/kb`)
		}
	})
	const toggleFavorite = api.knowledgeBase.toggleFavorite.useMutation({
		onSuccess: async () => {
			await utils.favorites.list.invalidate()
			await utils.knowledgeBase.get.invalidate({
				teamId: team?.id ?? "",
				documentId
			})
			await utils.knowledgeBase.list.invalidate({ teamId: team?.id ?? "" })
		}
	})

	useEffect(() => {
		if (
			document.data &&
			(document.data.id !== documentState?.id ||
				document.data.updatedAt.getTime() !== documentState.updatedAt.getTime())
		) {
			if (
				documentState?.id === document.data.id &&
				lastSyncedDocument.current &&
				JSON.stringify({
					...documentState,
					content: getEditorContentFingerprint(documentState.content)
				}) !==
					JSON.stringify({
						...lastSyncedDocument.current,
						content: getEditorContentFingerprint(
							lastSyncedDocument.current.content
						)
					})
			)
				return
			const nextState = {
				id: document.data.id,
				title: document.data.title,
				description: document.data.description ?? "",
				category: document.data.category ?? "",
				icon: document.data.icon ?? "FileText",
				content: document.data.content ?? null,
				accessRoleIds: document.data.accessRoles.map((role) => role.id),
				updatedAt: document.data.updatedAt
			}
			bodyRevisionRef.current = liveDocument.data?.contentRevision ?? 0
			lastSyncedDocument.current = nextState
			setDocumentState(nextState)
		}
	}, [document.data, documentState])

	useEffect(() => {
		if (!practice && document.data && documentId !== document.data.id) {
			navigate(`/${orgSlug}/${teamSlug}/kb/${document.data.id}`, {
				replace: true
			})
		}
	}, [practice, document.data?.id, documentId, navigate, orgSlug, teamSlug])

	useEffect(() => {
		if (!document.isLoading && document.data === null) {
			navigate(`/${orgSlug}/${teamSlug}/kb`)
		}
	}, [document.isLoading, document.data, navigate, orgSlug, teamSlug])

	const hasChanged = useMemo(() => {
		if (!document.data || !documentState) return false
		return (
			documentState.title !== document.data.title ||
			documentState.description !== (document.data.description ?? "") ||
			documentState.category !== (document.data.category ?? "") ||
			documentState.icon !== (document.data.icon ?? "FileText") ||
			(manualBody &&
				getEditorContentFingerprint(documentState.content) !==
					getEditorContentFingerprint(document.data.content)) ||
			documentState.accessRoleIds.slice().sort().join(",") !==
				document.data.accessRoles
					.map((role) => role.id)
					.sort()
					.join(",")
		)
	}, [document.data, documentState])

	if (isLoading || document.isLoading || !documentState || !document.data) {
		return <LoadingState className="bg-transparent" />
	}

	const selectedDocument = document.data
	const isEditing =
		canEdit && (!!practice || editingDocumentId === selectedDocument.id)
	const accessPolicy = selectedDocument.accessPolicy
	const accessExplanation = [
		accessPolicy.allTeamMembers
			? "All team members have KB access."
			: accessPolicy.teamRoles.length
				? `Team roles with KB access: ${accessPolicy.teamRoles.join(", ")}.`
				: "No team roles currently grant KB access.",
		documentState.accessRoleIds.length
			? `This document also requires one of these roles: ${team?.roles
					.filter((role) => documentState.accessRoleIds.includes(role.id))
					.map((role) => role.name)
					.join(", ")}.`
			: "This document has no additional role restrictions.",
		accessPolicy.allTeamMembersAreAdmins
			? "All team members are administrators and bypass document restrictions."
			: accessPolicy.teamAdminRoles.length
				? `Team admin roles bypass document restrictions: ${accessPolicy.teamAdminRoles.join(", ")}.`
				: null,
		accessPolicy.allOrganizationMembersAreAdmins
			? "All organization members have administrator access, even without team membership."
			: accessPolicy.organizationAdminRoles.length
				? `Organization admin roles always have access, even without team membership: ${accessPolicy.organizationAdminRoles.join(", ")}.`
				: null
	]
		.filter(Boolean)
		.join(" ")
	const reset = async () => {
		const nextState = {
			id: selectedDocument.id,
			title: selectedDocument.title,
			description: selectedDocument.description ?? "",
			category: selectedDocument.category ?? "",
			icon: selectedDocument.icon ?? "FileText",
			content: selectedDocument.content ?? null,
			accessRoleIds: selectedDocument.accessRoles.map((role) => role.id),
			updatedAt: selectedDocument.updatedAt
		}
		bodyRevisionRef.current = liveDocument.data?.contentRevision ?? 0
		lastSyncedDocument.current = nextState
		setDocumentState(nextState)
	}

	return (
		<main className="flex h-full flex-col">
			<AppHeader
				contentClassName="flex min-w-0 items-center gap-1.5"
				actions={
					practice ? (
						tutorialActions
					) : (
						<>
							<Button
								variant="ghost"
								size="icon-md"
								disabled={toggleFavorite.isPending}
								aria-label={
									selectedDocument.userHasFavorited
										? "Remove from favourites"
										: "Add to favourites"
								}
								aria-pressed={selectedDocument.userHasFavorited}
								onClick={() => {
									if (!team) return
									toggleFavorite.mutate({ teamId: team.id, documentId })
								}}
							>
								<StarIcon
									className={cn(
										"size-4",
										selectedDocument.userHasFavorited &&
											"fill-yellow-300 text-yellow-300"
									)}
								/>
							</Button>
							{canEdit && (
								<Button
									variant="outline"
									size="sm"
									disabled={editDocument.isPending}
									onClick={() => {
										if (isEditing && hasChanged) {
											toast.info(
												"Save or reset your changes before switching to View mode."
											)
											return
										}
										setEditingDocumentId(isEditing ? null : selectedDocument.id)
										posthog.capture("knowledge_base_mode_changed", {
											document_id: selectedDocument.id,
											team_id: team?.id,
											mode: isEditing ? "view" : "edit"
										})
									}}
								>
									{isEditing ? (
										<EyeIcon className="size-4" />
									) : (
										<PencilIcon className="size-4" />
									)}
									{isEditing ? "View" : "Edit document"}
								</Button>
							)}
							{team && isEditing ? (
								<>
									<KBShareDialog teamId={team.id} documentId={documentId}>
										<Button
											variant="ghost"
											size="sm"
											className="max-sm:size-8 max-sm:px-0"
											aria-label="Share document"
										>
											<LinkIcon className="size-4" />
											<span className="hidden sm:inline">Share</span>
										</Button>
									</KBShareDialog>
									<KBHistoryDialog
										teamId={team.id}
										documentId={documentId}
										onRestored={(slug) => {
											if (slug)
												navigate(`/${orgSlug}/${teamSlug}/kb/${slug}`, {
													replace: true
												})
										}}
									>
										<Button
											variant="ghost"
											size="icon-md"
											aria-label="Document history"
										>
											<HistoryIcon className="size-4" />
										</Button>
									</KBHistoryDialog>
									<Button
										variant="ghost"
										size="icon-md"
										aria-label={
											selectedDocument.pinned
												? "Unpin document"
												: "Pin document"
										}
										disabled={editDocument.isPending}
										onClick={() => {
											editDocument.mutate({
												teamId: team.id,
												documentId,
												pinned: !selectedDocument.pinned
											})
										}}
									>
										<PinIcon
											className={cn(
												"size-4",
												selectedDocument.pinned &&
													"fill-primary text-orbit-text"
											)}
										/>
									</Button>
									<Button
										variant="destructive-ghost"
										size="icon-md"
										aria-label="Delete document"
										disabled={deleteDocument.isPending}
										onClick={async () => {
											const confirmed = await confirmAction({
												title: "Delete document",
												message: "Delete this document?"
											})
											if (!confirmed) return
											deleteDocument.mutate({ teamId: team.id, documentId })
										}}
									>
										<Trash2Icon className="size-4" />
									</Button>
								</>
							) : null}
						</>
					)
				}
			>
				{team ? (
					<>
						<EntityIdentity
							kind="team"
							name={team.name}
							image={team.logo}
							className="hidden sm:inline-flex"
						/>
						<span className="hidden text-text-sub sm:inline">/</span>
						<h1 className="truncate font-medium text-text-main">
							Knowledge Base
						</h1>
					</>
				) : null}
			</AppHeader>
			<div className="@container/kb-content min-h-0 flex-1 overflow-y-auto px-7 py-5">
				<div className="mx-auto flex max-w-4xl flex-col gap-3">
					{tutorialPrompt}
					{isEditing ? (
						<>
							<Input
								value={documentState.title}
								disabled={!canEdit}
								onChange={(event) =>
									setDocumentState({
										...documentState,
										title: event.target.value
									})
								}
								className="text-3xl"
								placeholder="Document title"
								variant="title"
							/>
							<div className="grid gap-3 @2xl/kb-content:grid-cols-[minmax(0,1fr)_180px_180px]">
								<Textarea
									value={documentState.description}
									disabled={!canEdit}
									onChange={(event) =>
										setDocumentState({
											...documentState,
											description: event.target.value
										})
									}
									placeholder="Short list summary"
									className="min-h-10 resize-none disabled:opacity-100"
								/>
								<Input
									value={documentState.category}
									disabled={!canEdit}
									onChange={(event) =>
										setDocumentState({
											...documentState,
											category: event.target.value
										})
									}
									placeholder="Category"
								/>
								<KBIconPicker
									value={documentState.icon}
									disabled={!canEdit}
									onChange={(icon) =>
										setDocumentState({ ...documentState, icon })
									}
								/>
							</div>
							{canEdit && team?.roles?.length ? (
								<div className="flex items-center gap-2 rounded-lg border border-stroke bg-background px-3 py-2">
									<div className="flex min-w-0 grow items-center gap-2 text-sm text-text-sub">
										<ShieldIcon className="size-4" />
										<span className="truncate">
											{documentState.accessRoleIds.length
												? `Restricted to ${team.roles
														.filter((role) =>
															documentState.accessRoleIds.includes(role.id)
														)
														.map((role) => role.name)
														.join(", ")}`
												: "Visible to everyone with KB access"}
										</span>
										<TooltipButton
											variant="none"
											size="icon-xs"
											aria-label="Who can access this document"
											tooltip={accessExplanation}
										>
											<InfoIcon className="size-4 text-icons" />
										</TooltipButton>
									</div>
									<KBRolePicker
										roles={team.roles}
										selected={documentState.accessRoleIds}
										onChange={(accessRoleIds) =>
											setDocumentState({ ...documentState, accessRoleIds })
										}
									>
										<Button variant="outline" size="sm">
											Restrict roles
										</Button>
									</KBRolePicker>
								</div>
							) : null}
							<div
								data-onboarding-target={practice ? "article" : undefined}
								className="mt-4 rounded-lg surface-recessed bg-background-tertiary px-4 py-4"
							>
								<DocumentEditor
									commentRole={commentRole}
									onSaved={() => {
										void utils.knowledgeBase.get.invalidate({
											teamId: team?.id ?? "",
											documentId
										})
									}}
									key={selectedDocument.id}
									kind="document"
									id={selectedDocument.id}
									variant="document"
									referencesEnabled={!practice}
									mentionUsers={practice ? [] : undefined}
									templateKind={
										practice ? undefined : ContentTemplateKind.KnowledgeBase
									}
									teamId={team?.id ?? ""}
									placeholder="Write the page content, or press `/` for commands"
									initialState={documentState.content}
									disabled={!canEdit}
									onChange={(editorState) => {
										const nextContent = normalizeIssueDescription(editorState)

										setDocumentState((currentDocumentState) =>
											currentDocumentState
												? { ...currentDocumentState, content: nextContent }
												: currentDocumentState
										)
									}}
								/>
							</div>
						</>
					) : (
						<article className="flex flex-col gap-4">
							<KBIcon
								name={selectedDocument.icon}
								className="size-8 text-icons"
							/>
							<h2 className="text-3xl font-semibold break-words">
								{selectedDocument.title}
							</h2>
							{selectedDocument.description && (
								<p className="text-text-sub whitespace-pre-wrap">
									{selectedDocument.description}
								</p>
							)}
							{selectedDocument.category && (
								<p className="text-sm text-text-sub">
									{selectedDocument.category}
								</p>
							)}
							<DocumentEditor
								commentRole={commentRole}
								key={`${selectedDocument.id}:view:${selectedDocument.updatedAt.getTime()}`}
								kind="document"
								id={selectedDocument.id}
								variant="document"
								initialState={selectedDocument.content}
								disabled
								teamId={team?.id ?? ""}
								referencesEnabled={!practice}
							/>
						</article>
					)}
				</div>
			</div>
			<UnsavedChanges
				protectNavigation={!practice}
				isVisible={canEdit && hasChanged}
				canSave={!!documentState.title.trim()}
				onSave={async () => {
					if (!team) return
					if (
						lastSyncedDocument.current &&
						selectedDocument.updatedAt.getTime() !==
							lastSyncedDocument.current.updatedAt.getTime() &&
						!(await confirmAction({
							title: "Document changed",
							message:
								"This document changed since you started editing. Save your version over the latest changes? Choose Cancel, then Reset to load the latest version."
						}))
					)
						return
					const updated = await editDocument.mutateAsync({
						teamId: team.id,
						documentId,
						title: documentState.title,
						description: documentState.description || null,
						category: documentState.category || null,
						icon: documentState.icon || "FileText",
						content: manualBody ? documentState.content : undefined,
						contentRevision: manualBody ? bodyRevisionRef.current : undefined,
						accessRoleIds: documentState.accessRoleIds
					})
					if (updated) {
						const savedState = {
							...documentState,
							updatedAt: updated.updatedAt
						}
						lastSyncedDocument.current = savedState
						setDocumentState(savedState)
					}
				}}
				onReset={reset}
			/>
		</main>
	)
}
